/** Admin-импорт данных старого дашборда (data/dashboard-store.json → таблицы портала). */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload } from "lucide-react";
import React, { useRef, useState } from "react";
import { toast } from "sonner";

import * as api from "@/api/orders-planning";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";

const LABELS: Record<string, string> = {
  article_attrs: "Справочник артикулов",
  sales_plan: "Ячейки прогноза продаж",
  production_cells: "Ячейки плана производства",
  production_produced: "Отметки «произведено»",
  production_comments: "Комментарии производства",
  production_changes: "Подсветки правок",
  merge_confirmations: "Подтверждения объединений",
  channels: "Каналы",
  characteristics: "Характеристики",
  product_types: "Типы продукта",
  warehouse_clusters: "Кластеры складов",
  setting_stock_days: "Настройка запасов",
  setting_change_colors: "Цвета правок",
  control_items: "Позиции контроля",
  row_highlights: "Подсветки строк",
  action_log: "Записи журнала",
};

export function ImportPanel(): React.ReactElement {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [result, setResult] = useState<Record<string, number> | null>(null);

  const imp = useMutation({
    mutationFn: api.importStore,
    onSuccess: (r) => {
      setResult(r.imported);
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Импорт завершён");
    },
    onError: (e) => toast.error(extractErrorMessage(e, "Импорт не удался")),
  });

  return (
    <div className="space-y-3 rounded-md border bg-card p-3">
      <div className="text-sm font-medium">Импорт данных старого дашборда</div>
      <p className="max-w-2xl text-xs text-muted-foreground">
        Загрузите <code>data/dashboard-store.json</code> с сервера старого дашборда. Данные
        каждого присутствующего в файле раздела <b>полностью заменяют</b> текущие (планы,
        справочник, каталоги, контроль, журнал). Повторный импорт того же файла безопасен.
      </p>
      <Button
        onClick={() => fileRef.current?.click()}
        disabled={imp.isPending}
        className="gap-2"
      >
        <Upload className="size-4" />
        {imp.isPending ? "Импортирую…" : "Выбрать файл"}
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) imp.mutate(file);
        }}
      />
      {result && (
        <div className="grid max-w-xl grid-cols-2 gap-x-6 gap-y-1 text-xs">
          {Object.entries(result)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, count]) => (
              <div key={key} className="flex justify-between border-b border-dashed py-0.5">
                <span className="text-muted-foreground">{LABELS[key] ?? key}</span>
                <span className="font-medium tabular-nums">{count}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
